import { gql, useLazyQuery } from '@apollo/client';
import { OrganizationUserAccessTypes, RepCodeStates } from '@generated/graphql';
import type { RepCode } from '@interfaces/repCode';
import type { SxProps } from '@mui/material';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { usePermission } from '../../../hooks/usePermission';

const MAX_SEARCH_RESULTS = 12;

export type SearchResults = { items: RepCode[]; count: number };
export type SearchFn = (query: string) => Promise<SearchResults>;

export const SEARCH_REP_CODES = gql`
  query searchRepCodes($searchText: String!, $organizationId: ObjectID!, $states: [RepCodeStates!], $organizationUserId: ObjectID) {
    fetchRepCodes(input: {
      filter: { searchText: $searchText, organizationId: $organizationId, states: $states, organizationUserId: $organizationUserId }
      pagination: { perPage: ${MAX_SEARCH_RESULTS} },
    }) {
      totalCount
      repCodes {
        code
        name
      }
    }
  }
`;

const formatRepCode = (repCode: RepCode) =>
  repCode.name ? `${repCode.code} — ${repCode.name}` : (repCode.code ?? '');

const ACTIVE_REP_CODE_STATES = [RepCodeStates.Active];

export const RepCodeSelect = ({
  setRepCode,
  repCode,
  label,
  disabled,
  width,
  fullWidth,
  sx,
  error,
  errorText,
  autoFocus,
  onBlur,
  locked,
  lockMessage,
  testId,
  mode = 'search',
  placeholder,
  helpText,
  noOptionsText,
}: {
  setRepCode: (repCode: RepCode | undefined) => void;
  repCode: Partial<RepCode> | undefined;
  label: string;
  disabled?: boolean;
  width?: string;
  fullWidth?: boolean;
  sx?: SxProps;
  error?: boolean;
  errorText?: string;
  autoFocus?: boolean;
  locked?: boolean;
  lockMessage?: string;
  onBlur?: (e: React.FocusEvent) => void;
  testId?: string;
  /** 'assigned' renders a required picker (no free text, visible caret) instead of a search box. */
  mode?: 'search' | 'assigned';
  placeholder?: string;
  helpText?: string;
  noOptionsText?: string;
}) => {
  const canReadRepCode = usePermission('read:rep_code');
  const { activeOrganization, userContext } = useContext(UserContext);

  const organizationId = activeOrganization.id;
  const isRepCodeUser =
    userContext.accessType === OrganizationUserAccessTypes.RepCode;
  const organizationUserId = isRepCodeUser ? userContext.id : undefined;

  const [searchRepCodes] = useLazyQuery(SEARCH_REP_CODES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const searchFn = (query: string) =>
    new Promise<RepCode[]>((resolve) => {
      searchRepCodes({
        variables: {
          searchText: query,
          organizationId,
          states: ACTIVE_REP_CODE_STATES,
          organizationUserId,
        },
        onCompleted: (data) => resolve(data.fetchRepCodes.repCodes || []),
        onError: () => resolve([]),
      });
    });

  if (!canReadRepCode) return <></>;

  const isAssigned = mode === 'assigned';

  return (
    <Autocomplete
      width={width}
      testId={testId ?? 'rep-code-select-field'}
      freeSolo={!isAssigned}
      required={isAssigned}
      disabled={disabled}
      onSearch={searchFn}
      fullWidth={fullWidth}
      label={label ?? ''}
      placeholder={placeholder}
      helpText={helpText}
      noOptionsText={noOptionsText}
      value={repCode}
      getOptionLabel={(option) => option?.code}
      isOptionEqualToValue={(option: RepCode, value: RepCode) =>
        option.code === value.code
      }
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement>,
        option: RepCode,
      ) => <li {...props}>{formatRepCode(option)}</li>}
      onChange={(
        _e: React.SyntheticEvent,
        value: RepCode | string | null,
        reason: string,
      ) => {
        if (reason === 'clear') {
          setRepCode(undefined);
          return;
        }
        // freeSolo (search mode) emits the raw input string when the user types
        // something that is not an option; that is not a rep code selection.
        if (typeof value === 'string' || !value?.code) return;
        setRepCode(value);
      }}
      onBlur={onBlur}
      componentsProps={
        isAssigned ? undefined : { popupIndicator: { sx: { display: 'none' } } }
      }
      InputProps={{ disabled }}
      // Search mode locks the input after a pick; the picker must stay reopenable.
      inputReadOnly={!isAssigned && !!repCode}
      sx={sx}
      error={error}
      errorText={errorText}
      autoFocus={autoFocus}
      locked={locked}
      lockMessage={lockMessage}
      disableClearable={!repCode}
    />
  );
};

export default RepCodeSelect;
